import { useState, useEffect, useCallback } from "react";
import { trainingPlansApi } from "../lib/api";
import type { TrainingPlanWithMetrics, PlannedSession } from "@runlens/shared";

interface UseDashboardPlansResult {
  plans: TrainingPlanWithMetrics[];
  upcomingSessions: PlannedSession[];
  loading: boolean;
  error: string | null;
}

export function useDashboardPlans(): UseDashboardPlansResult {
  const [plans, setPlans] = useState<TrainingPlanWithMetrics[]>([]);
  const [upcomingSessions, setUpcomingSessions] = useState<PlannedSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { plans: activePlans } = await trainingPlansApi.list("active");

      if (activePlans.length === 0) {
        setPlans([]);
        setUpcomingSessions([]);
        setLoading(false);
        return;
      }

      // Fetch each active plan with 2 weeks of sessions
      const plansWithSessions = await Promise.all(
        activePlans.map((plan) => trainingPlansApi.get(plan.id, 2))
      );

      setPlans(plansWithSessions);

      // Compute next 3 upcoming sessions across all plans
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayStr = today.toISOString().split("T")[0];

      const upcoming = plansWithSessions
        .flatMap((plan) => plan.sessions)
        .filter(
          (s) =>
            s.scheduledDate >= todayStr &&
            s.status === "planned" &&
            s.sessionType !== "rest"
        )
        .sort((a, b) => a.scheduledDate.localeCompare(b.scheduledDate))
        .slice(0, 3);

      setUpcomingSessions(upcoming);
    } catch {
      setError("Failed to load training plans");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { plans, upcomingSessions, loading, error };
}
